import { Component, inject, Input } from '@angular/core';
import { Subject } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { ErrorHandlerService } from '../../../../_shared/services/error-handler.service';
import { ConfirmationModalService } from '../../../../_shared/services/modals/confirmation-modal.service';
import { ConfirmationOptions } from '../../../../_shared/models/modals/confirmation-options.model';
import { ComplianceDocumentsResponse } from '../../models/asset-details-response.model';
import { CommonModule } from '@angular/common';
import { EmptyStateComponent } from '../../../../_shared/components/states/empty-state/empty-state.component';
import { ComplianceDocumentRowComponent } from './compliance-document-row/compliance-document-row.component';
import { VehicleDocumentsService } from '../../services/vehicle-documents.service';
import { VehicleDocument } from '../../models/vehicle-documents.model';
import { UploadComplianceDocumentModalComponent } from './modals/upload-compliance-document-modal/upload-compliance-document-modal.component';
import { EditComplianceDocumentModalComponent } from './modals/edit-compliance-document-modal/edit-compliance-document-modal.component';
import { AttachmentViewModalComponent } from '../../../../_shared/components/modals/attachment-view-modal/attachment-view-modal.component';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-compliance',
  standalone: true,
  imports: [CommonModule, EmptyStateComponent, ComplianceDocumentRowComponent, TranslocoDirective],
  templateUrl: './compliance.component.html',
  styleUrl: './compliance.component.scss'
})
export class ComplianceComponent {
  private vehicleDocumentsService = inject(VehicleDocumentsService);
  private errorHandlerService = inject(ErrorHandlerService);
  private modalService = inject(NgbModal);
  private confirmationModalService = inject(ConfirmationModalService);
  private toastr = inject(ToastrService);
  private readonly transloco: TranslocoService = inject(TranslocoService);

  isLoading: boolean = true;
  complianceDocuments: ComplianceDocumentsResponse[] = [];
  private vehicleDocuments: VehicleDocument[] = [];
  @Input() assetId: number;

  private readonly cancelSubject = new Subject<void>();

  constructor() { }

  ngOnInit(): void {
    this.getComplianceInformation();
  }

  async getComplianceInformation(): Promise<void> {
    this.isLoading = true;

    try {
      this.cancelSubject.next();
      const response = await this.vehicleDocumentsService.getDocumentsByAssetId(this.assetId, this.cancelSubject);
      this.vehicleDocuments = response.data ?? [];
      this.complianceDocuments = this.vehicleDocuments.map(document => this.toComplianceDocument(document));

      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  openUploadDocumentModal(): void {
    const modalRef = this.modalService.open(UploadComplianceDocumentModalComponent, {
      centered: true,
      backdrop: 'static'
    });
    modalRef.componentInstance.assetId = this.assetId;

    modalRef.closed.subscribe((uploaded: boolean) => {
      if (uploaded) this.getComplianceInformation();
    });
  }

  onViewDocument(document: ComplianceDocumentsResponse): void {
    const vehicleDocument = this.vehicleDocuments.find(item => item.documentId === document.documentId);
    if (!vehicleDocument) return;

    const modalRef = this.modalService.open(AttachmentViewModalComponent, { centered: true, size: 'lg' });
    modalRef.componentInstance.fileUrl = vehicleDocument.viewUrl;
    modalRef.componentInstance.downloadUrl = vehicleDocument.downloadUrl;
    modalRef.componentInstance.fileName = `${vehicleDocument.documentName}${vehicleDocument.extension ?? ''}`;
  }

  onEditDocument(document: ComplianceDocumentsResponse): void {
    const vehicleDocument = this.vehicleDocuments.find(item => item.documentId === document.documentId);
    if (!vehicleDocument) return;

    const modalRef = this.modalService.open(EditComplianceDocumentModalComponent, {
      centered: true,
      backdrop: 'static'
    });
    modalRef.componentInstance.document = vehicleDocument;
    modalRef.componentInstance.assetId = this.assetId;

    modalRef.closed.subscribe((updated: boolean) => {
      if (updated) this.getComplianceInformation();
    });
  }

  onDeleteDocument(document: ComplianceDocumentsResponse): void {
    const options: ConfirmationOptions = {
      title: this.transloco.translate('assetOverview.compliance.deleteConfirm.title'),
      message: this.transloco.translate('assetOverview.compliance.deleteConfirm.message'),
      warningMessage: this.transloco.translate('assetOverview.compliance.deleteConfirm.warning'),
      confirmText: this.transloco.translate('assetOverview.compliance.deleteConfirm.confirm'),
      declineText: this.transloco.translate('assetOverview.compliance.deleteConfirm.decline'),
      confirmPlaceholder: this.transloco.translate('assetOverview.compliance.deleteConfirm.placeholder'),
      confirmationPrompt: 'delete',
      onConfirm: async () => {
        try {
          await this.vehicleDocumentsService.deleteDocument({
            documentId: document.documentId,
            assetId: this.assetId
          });
          this.toastr.success(this.transloco.translate('assetOverview.compliance.toasts.deleted'));
          this.getComplianceInformation();
        } catch (error) {
          this.errorHandlerService.handleError(error);
        }
      }
    };

    this.confirmationModalService.showConfirmationModal(options);
  }

  private toComplianceDocument(document: VehicleDocument): ComplianceDocumentsResponse {
    const isExpired = !!document.validUntil && new Date(document.validUntil) < new Date();

    return {
      documentId: document.documentId,
      documentName: document.documentName,
      complianceStandard: '',
      expirationDate: document.validUntil ? new Date(document.validUntil) : null,
      isExpired,
      isCompliant: !isExpired
    };
  }
}
